import { Controller } from "@hotwired/stimulus"
import { FileChecksum } from "@rails/activestorage/src/file_checksum"

const EXTENSION_CONTENT_TYPES = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  m4v: "video/x-m4v"
}

// Mini App uploader for target videos too big for Telegram's 20 MB bot limit.
// The file goes straight to storage through a presigned URL.
export default class extends Controller {
  static targets = ["fileInput", "fileName", "fileSize", "progress", "progressBar", "status", "uploadButton", "closeButton", "picker"]

  static values = {
    directUploadUrl: String,
    completeUrl: String,
    taskToken: String,
    maxBytes: Number,
    maxMb: Number,
    contentTypes: Array,
    copy: Object
  }

  connect() {
    window.Telegram?.WebApp?.ready()
    window.Telegram?.WebApp?.expand()
    this.initData = window.Telegram?.WebApp?.initData || new URLSearchParams(window.location.search).get("init_data") || ""
    this.file = null
    if (!this.initData) this.showStatus(this.text("missing_telegram"), true)
  }

  fileChanged() {
    this.file = null
    this.uploadButtonTarget.disabled = true
    const file = this.fileInputTarget.files[0]
    if (!file) return

    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = this.text("file_size", { size: this.formatMegabytes(file.size) })

    if (!this.contentTypesValue.includes(this.contentTypeFor(file))) {
      this.showStatus(this.text("unsupported"), true)
      return
    }
    if (file.size > this.maxBytesValue) {
      this.showStatus(this.text("too_large", { max_mb: this.maxMbValue }), true)
      return
    }

    this.file = file
    this.showStatus("")
    this.uploadButtonTarget.disabled = !this.initData
  }

  async upload() {
    if (!this.file || !this.initData) return

    this.setBusy(true)
    this.showStatus(this.text("preparing"))
    try {
      const checksum = await this.checksumFor(this.file)
      const direct = await this.postJson(this.directUploadUrlValue, {
        blob: {
          filename: this.file.name,
          byte_size: this.file.size,
          checksum,
          content_type: this.contentTypeFor(this.file)
        }
      })
      await this.putFile(direct.direct_upload)
      this.showStatus(this.text("checking"))
      const result = await this.postJson(this.completeUrlValue, { upload_token: direct.upload_token })
      this.finish(result.message)
    } catch (error) {
      this.setBusy(false)
      this.showStatus(error.message || this.text("generic_error"), true)
    }
  }

  close() {
    window.Telegram?.WebApp?.close()
  }

  finish(message) {
    this.progressTarget.classList.add("hidden")
    this.uploadButtonTarget.classList.add("hidden")
    this.pickerTarget.classList.add("hidden")
    this.closeButtonTarget.classList.remove("hidden")
    this.showStatus(message)
    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred("success")
  }

  checksumFor(file) {
    return new Promise((resolve, reject) => {
      FileChecksum.create(file, (error, checksum) => (error ? reject(new Error(this.text("generic_error"))) : resolve(checksum)))
    })
  }

  async postJson(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ init_data: this.initData, task_token: this.taskTokenValue, ...payload })
    }).catch(() => {
      throw new Error(this.text("upload_failed"))
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok || json.status !== "ok") throw new Error(json.message || this.text("generic_error"))
    return json
  }

  putFile({ url, headers }) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open("PUT", url, true)
      Object.entries(headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value))
      xhr.upload.addEventListener("progress", (event) => {
        if (!event.lengthComputable) return
        const percent = Math.round((event.loaded / event.total) * 100)
        this.progressBarTarget.style.width = `${percent}%`
        this.showStatus(this.text("uploading", { percent }))
      })
      const fail = () => reject(new Error(this.text("upload_failed")))
      xhr.addEventListener("load", () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : fail()))
      xhr.addEventListener("error", fail)
      xhr.addEventListener("abort", fail)
      xhr.addEventListener("timeout", fail)
      this.progressTarget.classList.remove("hidden")
      xhr.send(this.file.slice())
    })
  }

  // Decimal megabytes, the way Finder, iOS and Android show file sizes, so the
  // number matches the user's file picker and the bot's message.
  formatMegabytes(bytes) {
    return (bytes / 1_000_000).toFixed(1)
  }

  // Telegram's Android WebView often reports videos as application/octet-stream,
  // so a missing or generic type falls back to the file extension.
  contentTypeFor(file) {
    const type = (file.type || "").toLowerCase()
    if (type && !type.endsWith("/octet-stream")) return type
    const extension = file.name.split(".").pop().toLowerCase()
    return EXTENSION_CONTENT_TYPES[extension] || type
  }

  setBusy(busy) {
    this.uploadButtonTarget.disabled = busy
    this.fileInputTarget.disabled = busy
  }

  showStatus(message, isError = false) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("text-red-300", isError)
    this.statusTarget.classList.toggle("text-gray-300", !isError)
  }

  text(key, params = {}) {
    const template = this.copyValue[key] || ""
    return Object.entries(params).reduce((result, [name, value]) => result.split(`%{${name}}`).join(value), template)
  }
}
